import { PopoverBody, PopoverFooter, VStack } from '@chakra-ui/react';
import { useCallback, useState } from 'react';

import {
  type SchoolItem,
  SchoolSearchTree,
} from '../../../../../../shared/components/school-search-tree/school-search-tree';
import type { SchoolFilter } from '../../../types/filters';
import { FilterSelectFooter } from '../../FilterSelectFooter';

export interface SchoolSearchFilterFormProps {
  treeHeight?: number;
  onClose: () => void;
  filter: SchoolFilter;
  onSave: (filter: SchoolFilter) => void;
  isNewInstance?: boolean;
}

export const SchoolSearchFilterForm = ({
  treeHeight = 220,
  onClose,
  filter,
  onSave,
  isNewInstance,
}: SchoolSearchFilterFormProps) => {
  const defaultSelectedItems: Record<string, SchoolItem> =
    filter && 'schoolResultItems' in filter ? filter.schoolResultItems : {};

  const [selections, setSelections] =
    useState<Record<string, SchoolItem>>(defaultSelectedItems);

  const onClearSelections = useCallback(() => setSelections({}), []);

  const handleAddFilter = useCallback(() => {
    if (Object.keys(selections).length > 0) {
      // Omit isNot so FilterFormRender.handleSaveFilter uses the centralized isNotEnabled state
      const { isNot: _omit, ...filterWithoutIsNot } = filter;
      onSave({
        ...filterWithoutIsNot,
        schoolResultItems: selections,
        isUserSubmitted: true,
      });
    }
    // If no selections and it's a new instance, just close without saving
    // If it's an existing instance, the parent should handle removal
    onClose();
  }, [selections, filter, onSave, onClose]);

  const hasExistingSelections = Object.keys(defaultSelectedItems).length > 0;

  return (
    <VStack spacing={4} align="stretch">
      <PopoverBody padding={0}>
        <SchoolSearchTree
          selections={selections}
          setSelections={setSelections}
          treeHeight={treeHeight}
        />
      </PopoverBody>
      <PopoverFooter padding="0" borderTopWidth={0}>
        <FilterSelectFooter
          onClearSelections={onClearSelections}
          onClose={onClose}
          onAdd={handleAddFilter}
          addLabel={isNewInstance || !hasExistingSelections ? 'Add' : 'Update'}
          isAddDisabled={Object.keys(selections).length === 0}
        />
      </PopoverFooter>
    </VStack>
  );
};
